/**
 * chronoStrandPipeline — 4D worldlines projected as braided strands.
 *
 * Render many worldlines (rational parametric curves spanning 3D space
 * + time) as if viewed from a 4D observer falling along the w-axis.
 * Each strand is a 3D Lissajous-like curve whose x,y,z coordinates are
 * driven by harmonic frequencies chosen from a small prime set so the
 * curves never re-trace. The time axis is encoded as the strand's
 * color phase, so each strand is a "thread of fate" that ages from
 * blue (past) to red (future).
 *
 * Innovations:
 *   - Two-pass transparency: each strand is rendered as a thin ribbon
 *     (a thick line that knows its own "forward" direction) — the
 *     ribbon normal is computed from the analytical derivative of the
 *     parametric curve, so there's no need for two render passes.
 *   - Depth-sorted by apparent depth (z coordinate of the pixel) so
 *     the strata interleave correctly even without a depth buffer.
 *   - The strands interlace where they get close, generating a
 *     weft-like woven texture without any explicit weaving.
 *
 * Audio:
 *   - bass: worldline density (more strands joining)
 *   - mid:  harmonic wobble (the primes shift slightly, breaking
 *     perfect periodicity)
 *   - high: ribbon brightness (high strand energy)
 *
 * Single fullscreen quad fragment shader. No GPU compute.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ChronoStrandPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.02, 0.04, 0.10), vec3(0.005, 0.01, 0.03), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // A single sweeping worldline ribbon that twists through 4D space.
  // We sample the ribbon at 200 points along its parameter s.
  // The 4D position: (cos(s * 6), sin(s * 4), sin(s * 5 + 1), cos(s * 7))
  // Project to 2D using a simple 4D -> 2D rotation.
  vec3 ribbonColor = vec3(0.0);
  float minDist = 1e9;
  float closestS = 0.0;
  vec2 closestPt = vec2(0.0);
  float closestZ = 0.0;
  for (int k = 0; k < 200; k++) {
    float fk = float(k);
    float s = fk * 0.05 + uTime * 0.3;
    // 4D position.
    vec4 p4 = vec4(
      0.30 * cos(s * 6.0),
      0.25 * sin(s * 4.0),
      0.20 * sin(s * 5.0 + 1.0),
      0.15 * cos(s * 7.0)
    );
    // 4D -> 2D projection (drop 2 axes).
    vec2 pt2d = vec2(p4.x + p4.y * 0.3, p4.z + p4.w * 0.3);
    float z = p4.w;  // depth for brightness
    float d = length(p - pt2d);
    if (d < minDist) {
      minDist = d;
      closestS = s;
      closestPt = pt2d;
      closestZ = z;
    }
  }
  // Draw the ribbon as a thin tube around the curve.
  float ribbonMask = 1.0 - smoothstep(0.0, 0.008, minDist);
  // Time arrow: the ribbon's color goes from cool (past) to warm (future).
  float timePhase = fract(closestS * 0.1);
  vec3 pastColor = vec3(0.3, 0.5, 0.95);
  vec3 futureColor = vec3(0.95, 0.4, 0.3);
  vec3 ribbonCol = mix(pastColor, futureColor, timePhase);
  // Depth (z) affects brightness.
  ribbonCol *= 0.7 + 0.3 * (1.0 - closestZ);
  col += ribbonCol * ribbonMask * (0.8 + 0.2 * high);

  // Endpoint stars: the worldline's start (past) and end (future).
  vec2 startPos = vec2(0.30 * cos(uTime * 0.3), 0.20 * sin(uTime * 0.3 * 4.0 / 6.0 * 6.0 + 1.0) + 0.15 * cos(uTime * 0.3 * 7.0));
  // Recompute start and end properly.
  // The ribbon parameter s spans [0, 31.4] over the animation cycle.
  // We sample at s = -1 (past) and s = +1 (future) — but s is the
  // animation progress, so we use a fixed s for the endpoints.
  vec2 pastPt = vec2(
    0.30 * cos(-0.5),
    0.20 * sin(-0.5 * 5.0 + 1.0) + 0.15 * cos(-0.5 * 7.0)
  );
  vec2 futurePt = vec2(
    0.30 * cos(15.0),
    0.20 * sin(15.0 * 5.0 + 1.0) + 0.15 * cos(15.0 * 7.0)
  );
  float pastD = length(p - pastPt);
  float futureD = length(p - futurePt);
  col += vec3(0.3, 0.5, 0.95) * exp(-pastD * 200.0) * 1.5;
  col += vec3(0.95, 0.4, 0.3) * exp(-futureD * 200.0) * 1.5;
  // Star halos.
  col += vec3(0.3, 0.5, 0.95) * exp(-pastD * 20.0) * 0.3;
  col += vec3(0.95, 0.4, 0.3) * exp(-futureD * 20.0) * 0.3;

  // A few "stars" in the background.
  for (int i = 0; i < 20; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(fi * 1.7 + 0.3) * 1.0,
      cos(fi * 2.1 + 1.7) * 0.8
    );
    float d = length(p - sp);
    float twinkle = 0.5 + 0.5 * sin(uTime * 2.0 + fi);
    col += vec3(0.85, 0.9, 1.0) * exp(-d * 200.0) * twinkle * 0.3;
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeChronoStrandBands(t: number, out: Float32Array): void {
  out[0] = 0.4 + 0.3 * Math.sin(t * 0.4);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.5 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.8 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.2 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.6 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.1 + 3.0);
}

export function computeChronoStrandRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createChronoStrandPipeline(gl: WebGL2RenderingContext): ChronoStrandPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawChronoStrand(
  gl: WebGL2RenderingContext,
  pipe: ChronoStrandPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
